import React, { createContext } from "react";
import { DecodedAccessToken, LoginResponse, LoginRequest, MfaToken, ResetPasswordRequest } from "../../models";
import { Any } from "../../test-helpers/Any";

export interface AuthState {
    accessToken: string;
    decodedAccessToken: DecodedAccessToken;
    failedLoginAttempts: number;
    isAuthenticated: boolean;
    isLoggingOut: boolean;
    isLoggingIn: boolean;
    isRefreshing: boolean;
    refreshToken: string;
    loginMFAResponse: LoginResponse;
    loginCredentials: LoginRequest;
}

export interface IAuthContext extends AuthState {
    isAuthorized: boolean;
    setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
    authState: AuthState;
    isPublicComputer: boolean;
    setIsPublicComputer: React.Dispatch<React.SetStateAction<boolean>>;
    getMfaToken: (email: string) => MfaToken | undefined;
    setMfaToken: (mfaToken: MfaToken) => void;
    deleteMfaToken: (email: string) => void;
    forgotPassword: (emailAddress: string) => Promise<void>;
    resetPassword: (resetPasswordRequest: ResetPasswordRequest) => Promise<void>;
}

const defaultDecodedAccessToken: DecodedAccessToken = {
    provider: "",
    user_id: "",
    given_name: "",
    family_name: "",
    email: "",
    tenant_id: "",
    qr_code_image: "",
    manual_entry_code: "",
    nbf: -1,
    exp: -1,
    auth_time: -1,
} as const;

const defaultLoginMFAResponse: LoginResponse = {
    email: "",
    userId: -1,
    subjectId: "",
    mfaEnabled: true,
    mfaSetupComplete: false,
    mfaOptionId: 3,
    tenantId: -1,
    authenticatorQRCode: {
        qrCodeImageUrl: "",
        manualEntryCode: "",
    },
} as const;

const defaultLoginCredentials: LoginRequest = {
    email: "",
    password: "",
    tenantId: -1,
};

export const defaultAuthState: AuthState = {
    accessToken: "",
    decodedAccessToken: defaultDecodedAccessToken,
    failedLoginAttempts: 0,
    isAuthenticated: false,
    isLoggingIn: false,
    isLoggingOut: false,
    isRefreshing: false,
    refreshToken: "",
    loginMFAResponse: defaultLoginMFAResponse,
    loginCredentials: defaultLoginCredentials,
} as const;

const defaultValue: IAuthContext = {
    ...defaultAuthState,
    isAuthorized: false,
    setAuthState: Any.voidFn,
    authState: defaultAuthState,
    isPublicComputer: true,
    setIsPublicComputer: Any.voidFn,
    getMfaToken: () => undefined,
    setMfaToken: Any.voidFn,
    deleteMfaToken: Any.voidFn,
    forgotPassword: Any.voidPromise,
    resetPassword: Any.voidPromise,
};

export const AuthContext = createContext<IAuthContext>(defaultValue);
AuthContext.displayName = "Auth";
